import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { NoteStatus, PriorityLevel } from '@/types/conversation'

// Query key factory
export const noteListKeys = {
  all: ['notes-list'] as const,
  list: (params: NotesListParams) => [...noteListKeys.all, params] as const,
  one: (id: string | number) => [...noteListKeys.all, 'single', id] as const,
}

export interface NotesListFilters {
  assistant_id?: number[]
  status?: NoteStatus
  assigned_to?: number[]
  is_private?: boolean
  priority?: PriorityLevel
}

export interface NotesListParams {
  filters: NotesListFilters
  page: number
  perPage: number
  sort: { field: string; order: 'ASC' | 'DESC' }
}

/**
 * Note list item - uses snake_case to match backend API response directly.
 * This avoids unnecessary transformation and keeps frontend/backend aligned.
 */
export interface NoteListItem {
  id: string
  content: string
  status: NoteStatus
  priority?: PriorityLevel
  is_private: boolean
  created_by: number
  created_by_name: string
  created_at: string
  updated_at: string
  conversation_id: number
  assigned_to: number | null
  assigned_to_name?: string
  assigned_team_id: number | null
  assigned_team_name?: string
  assistant_id?: number
  assistant_name?: string
}

export interface NotesListResponse {
  data: NoteListItem[]
  total: number
}

/**
 * Hook to fetch a paginated list of notes
 */
export function useNotes(params: NotesListParams) {
  return useQuery({
    queryKey: noteListKeys.list(params),
    queryFn: async (): Promise<NotesListResponse> => {
      const { filters, page, perPage, sort } = params

      // Build react-admin format query params
      const queryParams = new URLSearchParams()
      queryParams.set('sort', JSON.stringify([sort.field, sort.order]))

      // Calculate range_ from page and perPage
      const start = (page - 1) * perPage
      const end = start + perPage - 1
      queryParams.set('range_', JSON.stringify([start, end]))

      // Build filter object for react-admin format
      const filter: Record<string, unknown> = {}

      // Handle assistant_id filter (via conversation relationship)
      // Use nested dict format for proper backend handling
      if (filters.assistant_id && filters.assistant_id.length > 0) {
        filter['conversation'] = { assistant_id: filters.assistant_id }
      }

      if (filters.status) {
        filter.status = filters.status
      }

      if (filters.assigned_to && filters.assigned_to.length > 0) {
        filter.assigned_to = filters.assigned_to
      }

      if (filters.is_private !== undefined) {
        filter.is_private = filters.is_private
      }

      if (filters.priority) {
        // Handle 'medium' priority which may be null in database
        if (filters.priority === 'medium') {
          filter._or = [
            { priority: 'medium' },
            { priority: null },
          ]
        } else {
          filter.priority = filters.priority
        }
      }

      queryParams.set('filter', JSON.stringify(filter))

      // Use react-admin endpoint format
      const response = await api.post(`/conversation-notes/ra/getList?${queryParams.toString()}`)
      const responseData = response.data

      // Minimal transformation - just format user names and ensure ID is string
      const notes: NoteListItem[] = (responseData.data || []).map((note: any): NoteListItem => ({
        id: note.id.toString(),
        content: note.content,
        status: note.status || 'none',
        priority: note.priority,
        is_private: note.is_private || false,
        created_by: note.created_by,
        created_by_name: note.created_by_user
          ? `${note.created_by_user.first_name} ${note.created_by_user.last_name}`
          : `User ${note.created_by}`,
        created_at: note.created_at,
        updated_at: note.updated_at,
        conversation_id: note.conversation_id,
        assigned_to: note.assigned_to,
        assigned_to_name: note.assigned_to_user
          ? `${note.assigned_to_user.first_name} ${note.assigned_to_user.last_name}`
          : undefined,
        assigned_team_id: note.assigned_team_id,
        assigned_team_name: note.assigned_to_team?.name,
        assistant_id: note.conversation?.assistant_id || note.conversation?.assistant?.id,
        assistant_name: note.conversation?.assistant?.name,
      }))

      return {
        data: notes,
        total: responseData.total || 0,
      }
    },
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Hook to fetch a single note by ID
 */
export function useNote(noteId: string | number | undefined) {
  return useQuery({
    queryKey: noteListKeys.one(noteId!),
    queryFn: async () => {
      const response = await api.post(`/conversation-notes/ra/getOne/${noteId}`)
      return response.data.data || response.data
    },
    enabled: !!noteId,
  })
}

/**
 * Hook to prefetch note data (for hover preview)
 */
export function usePrefetchNote() {
  const queryClient = useQueryClient()

  return (noteId: string | number) => {
    queryClient.prefetchQuery({
      queryKey: noteListKeys.one(noteId),
      queryFn: async () => {
        const response = await api.post(`/conversation-notes/ra/getOne/${noteId}`)
        return response.data.data || response.data
      },
      staleTime: 60 * 1000, // 1 minute
    })
  }
}
